import React, { useState, useEffect } from "react";
import {
  Select,
  Empty,
  notification,
  Button,
  Skeleton,
  Tabs,
  Card,
  Tooltip,
  Avatar,
  Dropdown,
  Menu,
  Popconfirm,
} from "antd";
import {
  CalendarOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  TeamOutlined,
  DownOutlined,
} from "@ant-design/icons";
import {
  FaBuilding,
  FaRegBuilding,
  FaUserMd,
  FaCalendarCheck,
  FaClock,
  FaChevronRight,
} from "react-icons/fa";
import PageTitle from "../../Ui/PageTitle";
import AddWorkingDayModal from "./AddWorkingDayModal";
import WorkingDayCard from "./WorkingDayCard";
import AvailabilityHoursModal from "./AvailabilityHoursModal";
import { useClinics } from "../../../hooks/useClinics";

import { useQueryClient } from "@tanstack/react-query";
import drEasyApi from "../../../Api/Customapi";
import {
  useGetClinicsShifts,
  useDeleteShiftSlot,
  useDeleteShift,
} from "../../../hooks/useShifts";
import TabPane from "antd/es/tabs/TabPane";
import ShiftFormModal from "../SpecialistsWorkingDays/CreateShiftModal";
import SlotFormModal from "../SpecialistsWorkingDays/SlotFormModal";

const PRIMARY_BTN =
  "bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all";

const WorkingDays = () => {
  const [selectedClinic, setSelectedClinic] = useState("all");
  const [workingDays, setWorkingDays] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availabilityModalVisible, setAvailabilityModalVisible] =
    useState(false);
  const [selectedDayForAvailability, setSelectedDayForAvailability] =
    useState(null);
  const [doctors, setDoctors] = useState([]);
  const [specialists, setSpecialists] = useState([]);

  // Specialists tab states
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [, setSelectedSpecialistId] = useState(null);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);

  const queryClient = useQueryClient();
  const token =
    localStorage.getItem("docTkn") || sessionStorage.getItem("docTkn");

  const { clinicsQuery } = useClinics({
    pageNumber: 1,
    pageSize: 100,
  });

  // Fetch specialists shifts
  const { data: shiftsData, isLoading: shiftsLoading } = useGetClinicsShifts({
    clinicId: selectedClinic === "all" ? undefined : selectedClinic,
    pageNumber: 1,
    pageSize: 100,
  });

  const deleteSlotMutation = useDeleteShiftSlot();

  const dayMap = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await drEasyApi.get("/api/Centers/getMyDoctors", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDoctors(response.data.doctors || []);
      } catch (error) {
        console.error("Error fetching doctors:", error);
        notification.error({
          message: "Error",
          description: "Failed to fetch doctors",
        });
      }
    };

    const fetchSpecialists = async () => {
      try {
        const response = await drEasyApi.get("/api/Centers/getMySpecialists", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSpecialists(response.data.specialists || []);
      } catch (error) {
        console.error("Error fetching specialists:", error);
        notification.error({
          message: "Error",
          description: "Failed to fetch specialists",
        });
      }
    };

    fetchDoctors();
    fetchSpecialists();
  }, [token]);

  const [editingShift, setEditingShift] = useState(null);
  const deleteShiftMutation = useDeleteShift();

  const handleEditShift = (shift) => {
    setEditingShift(shift);
    setIsShiftModalOpen(true);
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      await deleteShiftMutation.mutateAsync(shiftId);
    } catch (error) {}
  };

  const handleShiftModalClose = () => {
    setIsShiftModalOpen(false);
    setEditingShift(null);
  };

  // Extract working days from clinics
  useEffect(() => {
    if (
      clinicsQuery.data?.clinics &&
      Array.isArray(clinicsQuery.data.clinics)
    ) {
      const allWorkingDays = clinicsQuery.data.clinics.flatMap((clinic) => {
        if (!clinic) return [];

        // General days (no doctor)
        const generalDays = (clinic.genralDays || []).map((workingDay) => ({
          ...workingDay,
          id: workingDay.workingDayId,
          clinicName: clinic.clinicName || "Unknown Clinic",
          clinicId: clinic.clinicId,
          doctorId: null,
          doctorName: null,
          specialization: null,
          availabilityHours: workingDay.availabilityHours || [],
          isSpecialist: false,
          isGeneral: true,
        }));

        // Doctor-specific days
        const doctorDays = (clinic.doctors || []).flatMap((doctor) => {
          if (!doctor.workingDays || !Array.isArray(doctor.workingDays))
            return [];
          return doctor.workingDays.map((workingDay) => ({
            ...workingDay,
            id: workingDay.workingDayId,
            clinicName: clinic.clinicName || "Unknown Clinic",
            clinicId: clinic.clinicId,
            doctorId: doctor.doctorId,
            doctorName: doctor.doctorName,
            specialization: doctor.specialization,
            availabilityHours: workingDay.availabilityHours || [],
            isSpecialist: doctor.isSpecialist,
            isGeneral: false,
          }));
        });

        return [...generalDays, ...doctorDays];
      });

      setWorkingDays(allWorkingDays);
    } else {
      setWorkingDays([]);
    }
  }, [clinicsQuery.data]);

  const filteredWorkingDays =
    selectedClinic === "all"
      ? workingDays
      : workingDays.filter((day) => day.clinicId === selectedClinic);

  const handleAddWorkingDay = async (data) => {
    try {
      setIsSubmitting(true);

      const isDuplicate = workingDays.some(
        (wd) =>
          wd.clinicId === data.clinicId &&
          wd.dayOfWeek === data.dayOfWeek &&
          wd.doctorId === data.doctorId,
      );

      if (isDuplicate) {
        const clinic = clinicsQuery.data?.clinics?.find(
          (c) => c.clinicId === data.clinicId,
        );
        const doctor = doctors.find((d) => d.doctorId === data.doctorId);
        notification.error({
          message: "Error",
          description: `${clinic?.clinicName || "The clinic"} already has ${
            dayMap[data.dayOfWeek]
          } configured for Dr. ${doctor?.name || "Unknown"}`,
        });
        return;
      }

      const response = await drEasyApi.post(
        "/api/Clinic/AddWorkingDay",
        {
          clinicId: data.clinicId,
          dayOfWeek: data.dayOfWeek,
          doctorId: data.doctorId,
          isWorking: data.isWorking,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const clinic = clinicsQuery.data?.clinics?.find(
        (c) => c.clinicId === data.clinicId,
      );
      const doctor = doctors.find((d) => d.doctorId === data.doctorId);
      if (!clinic) return;

      const newWorkingDay = {
        clinicId: response.data.clinicId,
        dayOfWeek: response.data.dayOfWeek,
        isWorking: response.data.isWorking,
        doctorId: response.data.doctorId,
        clinicName: clinic.clinicName,
        doctorName: doctor?.name || "",
        id:
          response.data.id ||
          response.data.workingDayId ||
          `${data.clinicId}-${data.dayOfWeek}-${data.doctorId}`,
        workingDayId: response.data.workingDayId,
        maxPatients: null,
        availabilityHours: [],
      };

      setWorkingDays([...workingDays, newWorkingDay]);
      queryClient.invalidateQueries("clinics");

      notification.success({
        message: "Working day added",
        description: `${dayMap[data.dayOfWeek]} has been added for ${clinic.clinicName} (Dr. ${doctor?.name || "Unknown"})`,
      });

      setIsAddDialogOpen(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteWorkingDay = async (id) => {
    try {
      await drEasyApi.delete(`/api/Clinic/DeleteWorkingDay/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const deletedDay = workingDays.find(
        (wd) => wd.id === id || wd.workingDayId === id,
      );
      setWorkingDays(
        workingDays.filter((wd) => wd.id !== id && wd.workingDayId !== id),
      );

      queryClient.invalidateQueries("clinics");

      notification.success({
        message: "Working day removed",
        description: `${dayMap[deletedDay.dayOfWeek]} has been removed from ${deletedDay.clinicName} for Dr. ${deletedDay.doctorName}`,
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || error.message,
      });
    }
  };

  const toggleWorkingDayStatus = async (id) => {
    try {
      await drEasyApi.post(
        "/api/Clinic/ToggleWorkingDay",
        { workingDayId: id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setWorkingDays(
        workingDays.map((wd) =>
          wd.id === id || wd.workingDayId === id
            ? { ...wd, isWorking: !wd.isWorking }
            : wd,
        ),
      );

      queryClient.invalidateQueries("clinics");

      const toggledDay = workingDays.find(
        (wd) => wd.id === id || wd.workingDayId === id,
      );
      notification.success({
        message: "Status updated",
        description: `${dayMap[toggledDay.dayOfWeek]} for ${toggledDay.clinicName} (Dr. ${toggledDay.doctorName}) is now ${!toggledDay.isWorking ? "active" : "inactive"}`,
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || error.message,
      });
    }
  };

  const handleAvailability = (day) => {
    setSelectedDayForAvailability(day);
    setAvailabilityModalVisible(true);
  };

  // Specialists handlers
  const handleAddShiftClick = (specialistId) => {
    setSelectedSpecialistId(specialistId);
    setEditingShift(null);
    setIsShiftModalOpen(true);
  };

  const handleAddSlot = (dayId) => {
    setSelectedDayId(dayId);
    setEditingSlot(null);
    setIsSlotModalOpen(true);
  };

  const handleEditSlot = (dayId, slot) => {
    setSelectedDayId(dayId);
    setEditingSlot(slot);
    setIsSlotModalOpen(true);
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await deleteSlotMutation.mutateAsync(slotId);
    } catch (error) {}
  };

  const handleShiftSubmit = async () => {
    setIsShiftModalOpen(false);
    queryClient.invalidateQueries(["clinic-shifts"]);
  };

  // Dropdown menu for specialists
  const specialistsMenu = (
    <Menu
      items={
        specialists.length > 0
          ? specialists.map((specialist) => ({
              key: specialist.specialistId,
              label: (
                <div className="flex items-center space-x-2 py-1">
                  <Avatar
                    size="small"
                    icon={<UserOutlined />}
                    className="bg-primary"
                  />
                  <div>
                    <div className="font-medium">{specialist.name}</div>
                    {specialist.specialization && (
                      <div className="text-xs text-gray-500">
                        {specialist.specialization}
                      </div>
                    )}
                  </div>
                </div>
              ),
              onClick: () => handleAddShiftClick(specialist.specialistId),
            }))
          : [
              {
                key: "no-specialists",
                label: (
                  <span className="text-gray-400">
                    No specialists available
                  </span>
                ),
                disabled: true,
              },
            ]
      }
    />
  );

  const groupedWorkingDays = (clinicsQuery.data?.clinics || []).reduce(
    (acc, clinic) => {
      const clinicDays = filteredWorkingDays.filter(
        (day) => day.clinicId === clinic.clinicId,
      );
      if (clinicDays.length === 0) return acc;

      const generalDays = clinicDays.filter((day) => day.isGeneral);
      const doctorDays = clinicDays.filter((day) => !day.isGeneral);

      const doctorGroups = doctorDays.reduce((doctorAcc, day) => {
        const doctorKey = `${day.doctorId}-${day.doctorName}`;
        if (!doctorAcc[doctorKey]) {
          doctorAcc[doctorKey] = {
            doctorId: day.doctorId,
            doctorName: day.doctorName,
            specialization: day.specialization,
            isSpecialist: day.isSpecialist,
            isGeneral: false,
            days: [],
          };
        }
        doctorAcc[doctorKey].days.push(day);
        return doctorAcc;
      }, {});

      // Prepend a "General" pseudo-doctor group if there are general days
      const generalGroup =
        generalDays.length > 0
          ? {
              general: {
                doctorId: null,
                doctorName: "General",
                specialization: null,
                isSpecialist: false,
                isGeneral: true,
                days: generalDays,
              },
            }
          : {};

      acc[clinic.clinicId] = {
        clinicName: clinic.clinicName,
        doctors: Object.values({ ...generalGroup, ...doctorGroups }),
      };

      return acc;
    },
    {},
  );

  // Group specialists shifts data
  const groupedSpecialistsShifts = (shiftsData || []).reduce((acc, clinic) => {
    if (clinic.specialists && clinic.specialists.length > 0) {
      acc[clinic.clinicId] = {
        clinicId: clinic.clinicId,
        clinicName: clinic.clinicName,
        specialists: clinic.specialists.map((specialist) => ({
          ...specialist,
          shifts: specialist.shifts || [],
        })),
      };
    }
    return acc;
  }, {});

  if (clinicsQuery.isLoading) {
    return (
      <div className="animate-fade-in p-2 md:p-8">
        <PageTitle title="Working Days" subtitle="Loading..." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} active paragraph={{ rows: 4 }} />
          ))}
        </div>
      </div>
    );
  }

  if (clinicsQuery.isError) {
    return (
      <div className="animate-fade-in p-2 md:p-8">
        <PageTitle title="Working Days" subtitle="Error loading data" />
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {clinicsQuery.error.message}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const clinicFilter = (loading) => (
    <div className="mb-4 sm:mb-6">
      <div className="flex items-center gap-2 sm:justify-start">
        <FaBuilding className="text-primary text-xl sm:text-lg flex-shrink-0" />
        <span className="text-gray-700 hidden sm:inline-block">
          Filter by clinic:
        </span>
        <Select
          value={selectedClinic}
          onChange={setSelectedClinic}
          className="flex-1 sm:flex-none sm:w-60 min-w-0"
          placeholder="Select a clinic"
          loading={loading}
        >
          <Select.Option value="all">All Clinics</Select.Option>
          {clinicsQuery.data?.clinics?.map((clinic) => (
            <Select.Option key={clinic.clinicId} value={clinic.clinicId}>
              {clinic.clinicName}
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in p-2 md:p-8 overflow-x-hidden">
      <Tabs>
        {/* Doctors Tab */}
        <TabPane tab="Consultants Working Days" key="doctors">
          <PageTitle
            title="Consultants Working Days"
            actionLabel="Add Working Day"
            onAction={() => setIsAddDialogOpen(true)}
            subtitle="Configure working days for each doctor in each clinic"
          />

          {clinicFilter(clinicsQuery.isLoading)}

          {Object.keys(groupedWorkingDays).length === 0 ? (
            <Empty
              image={
                <CalendarOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />
              }
              description={
                selectedClinic === "all"
                  ? "No working days configured for any clinic"
                  : "No working days configured for this clinic"
              }
            >
              <Button
                type=""
                size="large"
                className={PRIMARY_BTN}
                onClick={() => setIsAddDialogOpen(true)}
                icon={<PlusOutlined />}
              >
                Add Working Day
              </Button>
            </Empty>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {Object.values(groupedWorkingDays).map((group) => (
                <WorkingDayCard
                  key={group.clinicName}
                  group={group}
                  dayMap={dayMap}
                  handleAvailability={handleAvailability}
                  toggleWorkingDayStatus={toggleWorkingDayStatus}
                  handleDeleteWorkingDay={handleDeleteWorkingDay}
                />
              ))}
            </div>
          )}

          <AvailabilityHoursModal
            visible={availabilityModalVisible}
            onCancel={() => setAvailabilityModalVisible(false)}
            workingDay={selectedDayForAvailability}
            onSuccess={() => {
              queryClient.invalidateQueries("clinics");
            }}
          />

          <AddWorkingDayModal
            isOpen={isAddDialogOpen}
            onClose={() => setIsAddDialogOpen(false)}
            onSubmit={handleAddWorkingDay}
            isSubmitting={isSubmitting}
            clinics={clinicsQuery.data?.clinics || []}
            doctors={doctors}
            specialists={specialists}
          />
        </TabPane>

        {/* Specialists Tab */}
        <TabPane tab="Specialists Working Days" key="specialists">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
            <PageTitle
              title="Specialists Working Days"
              subtitle="Manage shifts and schedules for specialists"
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingShift(null);
                setIsShiftModalOpen(true);
              }}
              type=""
              size="large"
              className={`w-full sm:w-auto ${PRIMARY_BTN}`}
            >
              Add Shift
            </Button>
          </div>

          {clinicFilter(shiftsLoading)}

          {shiftsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} active paragraph={{ rows: 4 }} />
              ))}
            </div>
          ) : Object.keys(groupedSpecialistsShifts).length === 0 ? (
            <Empty
              image={<FaUserMd style={{ fontSize: 48, color: "#bfbfbf" }} />}
              description="No specialists shifts configured"
            >
              {specialists.length > 0 && (
                <Dropdown overlay={specialistsMenu} trigger={["click"]}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{
                      backgroundColor: "#298d9f",
                      borderColor: "#298d9f",
                    }}
                  >
                    Add First Shift <DownOutlined />
                  </Button>
                </Dropdown>
              )}
            </Empty>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {Object.values(groupedSpecialistsShifts).map((group) => (
                <SpecialistsShiftCard
                  key={group.clinicName}
                  group={group}
                  dayMap={dayMap}
                  onAddSlot={handleAddSlot}
                  onEditSlot={handleEditSlot}
                  onDeleteSlot={handleDeleteSlot}
                  onEditShift={handleEditShift}
                  onDeleteShift={handleDeleteShift}
                />
              ))}
            </div>
          )}

          <ShiftFormModal
            visible={isShiftModalOpen}
            onCancel={handleShiftModalClose}
            onSubmit={handleShiftSubmit}
            specialists={specialists}
            editingShift={editingShift}
          />

          <SlotFormModal
            visible={isSlotModalOpen}
            onCancel={() => {
              setIsSlotModalOpen(false);
              setEditingSlot(null);
            }}
            dayId={selectedDayId}
            editingSlot={editingSlot}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default WorkingDays;

// ── Specialists Shift Card ────────────────────────────────────────────────────
const SpecialistsShiftCard = ({
  group,
  dayMap,
  onAddSlot,
  onEditSlot,
  onDeleteSlot,
  onEditShift,
  onDeleteShift,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <Card
      className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 rounded-2xl overflow-hidden [&_.ant-card-body]:!p-3 sm:[&_.ant-card-body]:!p-6"
      style={{
        background: "linear-gradient(135deg, #1F6F7D 0%, #298E9F 100%)",
      }}
    >
      <div
        className="bg-white bg-opacity-10 backdrop-blur-sm p-2 md:p-3 -m-3 mb-3 sm:-m-6 sm:mb-6 cursor-pointer hover:bg-opacity-20 transition-all duration-300"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="bg-white bg-opacity-20 p-2 sm:p-3 rounded-xl flex-shrink-0">
              <FaRegBuilding className="text-xl sm:text-2xl text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base md:text-2xl font-bold text-white mb-0 sm:mb-1 truncate">
                {group.clinicName}
              </h2>
              <div className="flex items-center space-x-2 md:space-x-4 text-white text-opacity-80 text-sm">
                <span>
                  <TeamOutlined className="mr-1" />
                  {group.specialists.length} Specialists
                </span>
              </div>
            </div>
          </div>
          <div
            className="text-white text-xl sm:text-2xl transition-transform duration-300 flex-shrink-0"
            style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
          >
            <FaChevronRight />
          </div>
        </div>
      </div>

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-none opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="space-y-4 sm:space-y-6">
          {group.specialists.map((specialist) => (
            <div
              key={specialist.specialistId}
              className="bg-white backdrop-blur-sm rounded-2xl p-3 sm:p-6"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <Avatar
                    size={{ xs: 44, sm: 64 }}
                    icon={<UserOutlined />}
                    className="bg-primary border-2 sm:border-4 border-primary shadow-lg flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-xl font-semibold text-primary mb-0 sm:mb-1 truncate">
                      {specialist.name}
                    </h3>
                    {specialist.specialization && (
                      <span className="text-xs sm:text-sm text-gray-600">
                        {specialist.specialization}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {specialist.shifts && specialist.shifts.length > 0 ? (
                <div className="space-y-4">
                  {specialist.shifts.map((shift, idx) => (
                    <div
                      key={shift.shiftId}
                      className="border rounded-xl p-3 sm:p-5 bg-gradient-to-br from-gray-50 to-white shadow-sm"
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between mb-3 sm:mb-4 pb-3 border-b">
                        <h3 className="font-semibold text-gray-800 text-base sm:text-lg">
                          Shift Schedule ({idx + 1})
                        </h3>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-600 mb-3">
                          Active Days
                        </p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                          {shift.days?.map((day) => (
                            <div
                              key={day.dayId}
                              className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-md transition-shadow"
                            >
                              <div className="flex flex-col items-center space-y-1">
                                <span className="text-xs font-semibold text-gray-700">
                                  {dayMap[day.dayOfWeek]}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Time slots */}
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                          <p className="text-sm font-medium text-gray-600 m-0">
                            Time Slots ({shift.hours?.length || 0})
                          </p>

                          <div className="flex flex-wrap items-center gap-1">
                            <Button
                              type=""
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => onAddSlot(shift.shiftId)}
                              className="bg-primary text-white hover:bg-primary_hover shadow-sm"
                            >
                              Add Slot
                            </Button>
                            <Tooltip title="Edit Shift">
                              <Button
                                type=""
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() =>
                                  onEditShift({
                                    ...shift,
                                    specialistId: specialist.specialistId,
                                    clinicId: group.clinicId,
                                  })
                                }
                                className="hover:bg-primary/10 hover:text-primary !border !border-gray-300"
                              >
                                Edit Shift
                              </Button>
                            </Tooltip>
                            <Popconfirm
                              title="Delete this shift?"
                              description="All its days and time slots will be removed."
                              okText="Delete"
                              okButtonProps={{ danger: true }}
                              cancelText="Cancel"
                              onConfirm={() => onDeleteShift(shift.shiftId)}
                            >
                              <Tooltip title="Delete Shift">
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  className="hover:bg-red-50"
                                />
                              </Tooltip>
                            </Popconfirm>
                          </div>
                        </div>

                        {shift.hours && shift.hours.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {shift.hours.map((slot) => (
                              <div
                                key={slot.hourId}
                                className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3 hover:border-primary/40 transition-colors"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center text-gray-700 min-w-0">
                                    <FaClock className="mr-2 text-primary flex-shrink-0" />
                                    <span className="font-medium text-xs sm:text-sm">
                                      {formatTime(slot.startTime)} -{" "}
                                      {formatTime(slot.endTime)}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-1 flex-shrink-0">
                                    <Tooltip title="Edit">
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<ClockCircleOutlined />}
                                        onClick={() =>
                                          onEditSlot(shift.shiftId, slot)
                                        }
                                        className="hover:bg-primary/10 hover:text-primary"
                                      />
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => onDeleteSlot(slot.hourId)}
                                        className="hover:bg-red-50"
                                      />
                                    </Tooltip>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center bg-gray-50 border border-dashed border-gray-300 rounded-lg py-6">
                            <FaClock className="text-3xl text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">
                              No time slots configured
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Click "Add Slot" to get started
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <FaCalendarCheck className="text-4xl sm:text-5xl text-gray-300 mx-auto mb-3" />
                  <h4 className="text-base font-semibold text-gray-600 mb-1">
                    No shifts configured
                  </h4>
                  <p className="text-sm text-gray-400">
                    Add your first shift to start scheduling
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};